import { elementHasScrollX, elementHasScrollY } from '@/utils/dom';
import '@/components/scroll-indicator/scroll-indicator.vue';

export default {
    name: 'PendoScrollIndicator',
    props: {
        disabled: {
            type: Boolean
        }
    },
    data () {
        return {
            canScroll: {
                up: false,
                down: false,
                left: false,
                right: false
            }
        };
    },
    watch: {
        async disabled () {
            await this.$nextTick();
            this.setupObservers();
        }
    },
    mounted () {
        this.setupObservers();
    },
    methods: {
        setupObservers () {
            if (this.disabled) {
                return;
            }

            if (!this.resizeObserver) {
                this.resizeObserver = new ResizeObserver(this.toggleIndicators);
                this.$once('hook:beforeDestroy', () => this.resizeObserver.disconnect());
            }
            this.resizeObserver.observe(this.$refs.content);

            if (!this.mutationObserver) {
                this.mutationObserver = new MutationObserver(this.toggleIndicators);
                this.$once('hook:beforeDestroy', () => this.mutationObserver.disconnect());
            }
            this.mutationObserver.observe(this.$refs.content, {
                childList: true,
                attributes: false
            });
        },
        toggleIndicators () {
            const contentRef = this.$refs.content;
            if (!contentRef) {
                return;
            }

            const hasHorizontalScrollbar = elementHasScrollX(contentRef);
            const hasVerticalScrollbar = elementHasScrollY(contentRef);

            const isScrolledToTopEdge = contentRef.scrollTop === 0;
            const isScrolledToLeftEdge = contentRef.scrollLeft === 0;

            // check for < 1px to ensure subpixels are accounted for i.e. treat 603.5px scrollHeight equal to 604px offsetHeight
            const isScrolledToRightEdge = contentRef.scrollWidth - contentRef.scrollLeft - contentRef.offsetWidth < 1;
            const isScrolledToBottomEdge = contentRef.scrollHeight - contentRef.scrollTop - contentRef.offsetHeight < 1;

            this.canScroll.up = hasVerticalScrollbar && !isScrolledToTopEdge;
            this.canScroll.down = hasVerticalScrollbar && !isScrolledToBottomEdge;
            this.canScroll.right = hasHorizontalScrollbar && !isScrolledToRightEdge;
            this.canScroll.left = hasHorizontalScrollbar && !isScrolledToLeftEdge;
        }
    },
    render (createElement) {
        if (this.disabled) {
            const nodes = this.$scopedSlots && this.$scopedSlots.default();
            if (!nodes) {
                return createElement();
            }

            if (nodes.length < 2 && !nodes[0].text) {
                return nodes;
            }

            return createElement('div', nodes);
        }

        const renderIndicators = () => {
            if (this.$scopedSlots.indicators) {
                return this.$scopedSlots.indicators({ canScroll: this.canScroll });
            }

            return ['up', 'down', 'left', 'right'].map((direction) => {
                return createElement('div', {
                    class: [
                        'pendo-scroll-indicator__shadow',
                        `pendo-scroll-indicator__shadow--${direction}`,
                        {
                            'is-active': this.canScroll[direction]
                        }
                    ]
                });
            });
        };

        return createElement(
            'div',
            {
                class: 'pendo-scroll-indicator'
            },
            [
                createElement(
                    'div',
                    {
                        class: 'pendo-scroll-indicator__content',
                        ref: 'content',
                        on: {
                            // & = passive modifier
                            '&scroll': this.toggleIndicators
                        }
                    },
                    [this.$slots.default, renderIndicators()]
                )
            ]
        );
    }
};
